import { useAppDispatch } from 'client/hooks/use-dispatch'
import { useWebsiteSettings } from 'client/store'
import { PageLocaleEnum } from 'common/enums/PageLocaleEnum'
import React, { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutationWithValidation } from 'src/editor/api/hooks/use-api-mutation-with-validation'
import { websitePagesQuery } from 'src/editor/api/queries/website-pages-query'
import { addWebsiteLocale } from 'src/editor/api/website-websites-api'
import { LanguageSelector } from 'src/editor/shared/ui-lib/language-selector'
import { getLocalesOptions } from 'src/editor/shared/ui-lib/language-selector/utils/get-locales-options'
import { addWebsiteLayout } from 'src/editor/store/website-settings/website-settings-actions'
import {
  getWebsiteId,
  getWebsiteLocale,
  getWebsiteLocales,
} from 'src/editor/store/website-settings/website-settings-selector'
import { GroupTitle } from '../../settings/group-title'
import { AddWebsiteLocale } from './add-website-locale'
import { RemoveWebsiteLocale } from './remove-website-locale'

export const WebsiteLocalesSelector = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const websiteId = useWebsiteSettings(getWebsiteId) as number
  const websiteLocale = useWebsiteSettings(getWebsiteLocale)
  const websiteLocales = useWebsiteSettings(getWebsiteLocales) || []

  const [localeToRemove, setLocaleToRemove] = useState<PageLocaleEnum | null>(
    null,
  )
  const [copyPagesLocale, setCopyPagesLocale] = useState<PageLocaleEnum | null>(
    null,
  )
  const pendingLocaleToAddRef = useRef<PageLocaleEnum | null>(null)

  const { fetcher: fetcherAddLocale, isLoading: isLoadingAddLocale } =
    useMutationWithValidation({
      requestFunction: addWebsiteLocale,
      successToastMessage: t('website_settings.add_locale.added_successfully'),
      handleSuccess: (response, [{ locale }]) => {
        dispatch(
          addWebsiteLayout({
            locale,
            layoutPageId: response.layoutPageContentId,
          }),
        )
        setCopyPagesLocale(locale)
      },
      handleFinally: () => {
        pendingLocaleToAddRef.current = null
      },
      invalidateKeyOnSuccess: websitePagesQuery.getQueryKey(websiteId),
      retry: false,
    })

  const localesOptions = useMemo(() => {
    return getLocalesOptions({
      t,
      setAdditionalProperties: locale => ({
        nonRemovable: websiteLocale === locale,
      }),
    })
  }, [t, websiteLocale])

  const handleChangeLocales = (locales: PageLocaleEnum[]) => {
    if (websiteLocales.length > locales.length) {
      const localeToRemove = websiteLocales.find(
        locale => !locales.includes(locale),
      )
      if (localeToRemove) {
        setLocaleToRemove(localeToRemove)
      }
    } else if (websiteLocales.length < locales.length) {
      const localeToAdd = locales.find(
        locale => !websiteLocales.includes(locale),
      )
      if (localeToAdd && pendingLocaleToAddRef.current !== localeToAdd) {
        pendingLocaleToAddRef.current = localeToAdd
        fetcherAddLocale({
          id: websiteId,
          locale: localeToAdd,
        })
      }
    }
  }

  return (
    <>
      <GroupTitle withoutHorizontalLine>
        {t('website_settings.website_languages.group_title')}
      </GroupTitle>

      <LanguageSelector
        isMulti
        isLoading={isLoadingAddLocale}
        isDisabled={isLoadingAddLocale || !!localeToRemove}
        selectedLocales={websiteLocales}
        options={localesOptions}
        onChange={handleChangeLocales}
        labelText={t('website_settings.website_languages.label')}
      />
      <AddWebsiteLocale
        copyPagesLocale={copyPagesLocale}
        onClose={() => setCopyPagesLocale(null)}
      />
      <RemoveWebsiteLocale
        localeToRemove={localeToRemove}
        onClose={() => setLocaleToRemove(null)}
      />
    </>
  )
}
